/**
 * TV / 10-foot odak gezinmesi.
 *
 * ## Neden?
 *
 * Geniş ekran tek başına TV değildir; geniş bir masaüstü monitöründe ok
 * tuşlarıyla sayfa kaydırmak beklenen davranıştır. Bu kanca yalnız açık TV
 * tercihi, TV tarayıcısı veya kumandaya özgü tuş kanıtıyla etkinleşir
 * (`tvMode.ts`).
 *
 * ## Kural
 *
 * - Ok tuşu, odağa en yakın uygun öğeye taşınır; aday yoksa varsayılan
 *   kaydırma korunur.
 * - Form öğeleri ve oynatıcı (`.watch`) dışarıda bırakılır.
 * - Odak hedefi görünür alanın ortasına kaydırılır; sabit üst/alt yüzeylerin
 *   altında kalmaz.
 * - Rota değişiminde son odak anahtarı hatırlanır; detaydan dönüşte aynı
 *   karta odaklanılır.
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

type Direction = 'up' | 'down' | 'left' | 'right';

function directionOf(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowDown':
      return 'down';
    case 'ArrowLeft':
      return 'left';
    case 'ArrowRight':
      return 'right';
    default:
      return null;
  }
}

function isVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && getComputedStyle(element).visibility !== 'hidden';
}

/** Form/oynatıcı içinde ok tuşları kendi anlamını taşır. */
function isTypingTarget(target: HTMLElement | null): boolean {
  if (target === null) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable ||
    (typeof target.closest === 'function' && target.closest('.watch') !== null)
  );
}

/** Eski TV tarayıcılarında focus/scroll seçenekleri desteklenmeyebilir. */
function safeFocus(element: HTMLElement): void {
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function safeScrollIntoView(element: HTMLElement): void {
  try {
    element.scrollIntoView({ block: 'center', inline: 'nearest' });
  } catch {
    // Eski motor seçenek nesnesini kabul etmiyorsa kaydırma opsiyonel kalır.
  }
}

/**
 * Odak anahtarı: rota değişiminde aynı öğeyi bulmak için kararlı bir kimlik.
 *
 * Kartlarda `data-item-id` varken metin kullanmaya gerek yok; diğer
 * düğmelerde `id`, `aria-label` veya `href` yeterlidir.
 */
export function focusKey(element: HTMLElement): string {
  const candidates = [
    element.dataset.tvFocus,
    element.dataset.itemId,
    element.id,
    element.getAttribute('aria-label'),
    element.getAttribute('href'),
  ];
  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && candidate.length > 0) return candidate;
  }
  return `${element.tagName}:${(element.textContent ?? '').trim().slice(0, 48)}`;
}

function collectCandidates(current: HTMLElement): Array<{ element: HTMLElement; rect: DOMRect }> {
  return Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((element) => element !== current && isVisible(element))
    .map((element) => ({ element, rect: element.getBoundingClientRect() }));
}

/** Adaylar arasından yöne göre en yakın öğeyi seçer (saf, test edilebilir). */
export function pickNext(
  current: DOMRect,
  candidates: Array<{ element: HTMLElement; rect: DOMRect }>,
  direction: Direction,
): HTMLElement | null {
  let best: HTMLElement | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const { element, rect } = candidate;
    const currentX = current.left + current.width / 2;
    const currentY = current.top + current.height / 2;
    const candidateX = rect.left + rect.width / 2;
    const candidateY = rect.top + rect.height / 2;

    let inDirection = false;
    let primary = 0;
    let secondary = 0;

    if (direction === 'right') {
      inDirection = rect.left >= current.right - 2;
      primary = Math.abs(rect.left - current.right);
      secondary = Math.abs(candidateY - currentY);
    } else if (direction === 'left') {
      inDirection = rect.right <= current.left + 2;
      primary = Math.abs(current.left - rect.right);
      secondary = Math.abs(candidateY - currentY);
    } else if (direction === 'down') {
      inDirection = rect.top >= current.bottom - 2;
      primary = Math.abs(rect.top - current.bottom);
      secondary = Math.abs(candidateX - currentX);
    } else {
      inDirection = rect.bottom <= current.top + 2;
      primary = Math.abs(current.top - rect.bottom);
      secondary = Math.abs(candidateX - currentX);
    }

    if (!inDirection) continue;
    const score = primary + secondary * 2;
    if (score < bestScore) {
      bestScore = score;
      best = element;
    }
  }

  return best;
}

function hasModifier(event: KeyboardEvent): boolean {
  return event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

function focusRoot(): ParentNode {
  return document.querySelector<HTMLElement>('.content') ?? document;
}

function focusablesIn(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
}

function moveFocus(direction: Direction): boolean {
  const current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  if (current === null || current === document.body) {
    const first = focusablesIn(focusRoot())[0];
    if (first === undefined) return false;
    safeFocus(first);
    safeScrollIntoView(first);
    return true;
  }

  const next = pickNext(current.getBoundingClientRect(), collectCandidates(current), direction);
  if (next === null) return false;

  safeFocus(next);
  safeScrollIntoView(next);
  return true;
}

export function useSpatialNavigation(enabled: boolean): void {
  const memoryRef = useRef(new Map<string, string>());

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const restoreFocus = (): void => {
      if (document.querySelector('[role="dialog"][aria-modal="true"]') !== null) return;
      const root = focusRoot();
      const elements = focusablesIn(root);
      const saved = memoryRef.current.get(window.location.hash || '#/');
      const target =
        saved === undefined ? elements[0] : elements.find((element) => focusKey(element) === saved);
      if (target === undefined) return;
      safeFocus(target);
      safeScrollIntoView(target);
    };

    const scheduleRestore = (): void => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(restoreFocus);
      });
    };

    const onFocusIn = (event: FocusEvent): void => {
      if (event.target instanceof HTMLElement) {
        memoryRef.current.set(window.location.hash || '#/', focusKey(event.target));
      }
    };

    const onHashChange = (): void => {
      scheduleRestore();
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || hasModifier(event)) return;

      // TV kumandalarında "geri" tuşu tarayıcı geçmişini kullanır.
      if (event.key === 'GoBack' || event.key === 'BrowserBack') {
        event.preventDefault();
        window.history.back();
        return;
      }

      const direction = directionOf(event.key);
      if (direction === null || isTypingTarget(event.target as HTMLElement | null)) return;

      if (moveFocus(direction)) event.preventDefault();
    };

    window.addEventListener('focusin', onFocusIn);
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('keydown', onKeyDown);
    if (document.activeElement === null || document.activeElement === document.body) {
      scheduleRestore();
    }

    return () => {
      window.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled]);
}
